import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Button,
    Typography,
    IconButton
} from "@mui/material";
import { useAtom } from "jotai";
import { isMbxUserClient, mbxUserAtom } from "../atoms/atoms";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import ExportDateRangePicker from "../utils/CommonComonents/ExportDateRangePicker";
import { SmallSpinner } from "../utils/CommonComponents/Spinner";
import { execReadBySortkey } from "../modules/DBService";
import { createTimeDisplayInfoForTimeZone } from "../modules/TimeService";
import { bookingByCompany } from "../queries/ListBookingsQueries";
import { CSVLink } from "react-csv";
import moment from "moment";
import { format, addDays } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { escapeQuotes } from "../utils";

const csvHeaders = [
    { label: "ID", key: "ID" },
    { label: "Order number", key: "ORDERNUMBER" },
    { label: "Date created", key: "DATECREATED" },
    { label: "Date updated", key: "DATEUPDATED" },
    { label: "Start date", key: "STARTDATE" },
    { label: "Service name", key: "SERVICENAME" },
    { label: "Duration", key: "DURATION" },
    { label: "Booking address", key: "BOOKINGADDRESS" },
    { label: "Booking state/province", key: "BOOKINGPROVSTATE" },
    { label: "Booking zip/postal code", key: "BOOKINGPOSTALCODE" },
    { label: "Booking country", key: "BOOKINGCOUNTRY" },
    { label: "Booked by", key: "ADMINEMAIL" },
    { label: "Status", key: "STATUS" },
    { label: "Cancelled by", key: "CANCELLEDBY" },
    { label: "Initial charge", key: "INITIALCHARGE" },
    { label: "Additional charge", key: "ADDITIONALCHARGE" },
    { label: "Provider tip", key: "PROVIDERTIP" },
    { label: "Booking rating", key: "RATING" },
    { label: "Booking notes", key: "NOTES" },
    { label: "Client name", key: "CLIENTNAME" },
    { label: "Client first name", key: "CLIENTFIRSTNAME" },
    { label: "Client last name", key: "CLIENTLASTNAME" },
    { label: "Client email", key: "CLIENTEMAIL" },
    { label: "Client phone", key: "CLIENTPHONENUMBER" },
    { label: "Client notes", key: "CLIENTNOTES" },
    { label: "Provider name", key: "PROVIDERNAME" },
    { label: "Provider first name", key: "PROVIDERFIRSTNAME" },
    { label: "Provider last name", key: "PROVIDERLASTNAME" },
    { label: "Provider email", key: "PROVIDEREMAIL" },
    { label: "Provider phone", key: "PROVIDERPHONENUMBER" }
];

function ExportBookingList({ open, onClose, serviceTypeOption }) {
    const [exportFromDate, setExportFromDate] = useState(new Date());
    const [exportToDate, setExportToDate] = useState(addDays(new Date(), 15));
    const [preparingExportData, setPreparingExportData] = useState(false);
    const [showExportSpinner, setShowExportSpinner] = useState(false);
    const [exportReadyBookings, setExportReadyBookings] = useState([]);
    const [isClientLoggedIn] = useAtom(isMbxUserClient);
    const [mbxUser] = useAtom(mbxUserAtom);

    const csvData = (bookingList) => {
        try {
            let rows = [];
            if (bookingList.items && bookingList.items.length > 0) {
                bookingList.items.map(async (item) => {
                    enrichBookingData(item);
                });
                rows = bookingList.items;
            }
            const csvData = rows.map((item) => {
                const {
                    id: ID,
                    serviceName: SERVICENAME,
                    startdate: STARTDATE,
                    duration: DURATION,
                    adminEmail: ADMINEMAIL,
                    status: STATUS,
                    cancelledByEmail: CANCELLEDBY,
                    initialCharge: INITIALCHARGE,
                    additionalCharges: ADDITIONALCHARGE,
                    orderNumber: ORDERNUMBER,
                    dateCreated: DATECREATED,
                    updatedAt: DATEUPDATED,
                    bookingAddressoneLine: BOOKINGADDRESS,
                    bookingState: BOOKINGPROVSTATE,
                    bookingPostalCode: BOOKINGPOSTALCODE,
                    bookingCountry: BOOKINGCOUNTRY,
                    clientName: CLIENTNAME,
                    clientFirstName: CLIENTFIRSTNAME,
                    clientLastName: CLIENTLASTNAME,
                    clientEmail: CLIENTEMAIL,
                    clientPhoneNumber: CLIENTPHONENUMBER,
                    clientNotes: CLIENTNOTES,
                    providerName: PROVIDERNAME,
                    providerFirstName: PROVIDERFIRSTNAME,
                    providerLastName: PROVIDERLASTNAME,
                    providerEmail: PROVIDEREMAIL,
                    providerPhoneNumber: PROVIDERPHONENUMBER,
                    ratingstars: RATING,
                    providerTip: PROVIDERTIP,
                    note: NOTES
                } = item;

                const newItem = {
                    ID,
                    SERVICENAME,
                    STARTDATE: moment(STARTDATE).format("YYYY-MM-DD hh:mm A"),
                    DURATION,
                    BOOKINGADDRESS,
                    BOOKINGPROVSTATE,
                    BOOKINGPOSTALCODE,
                    BOOKINGCOUNTRY,
                    ADMINEMAIL: ADMINEMAIL === "" ? "client" : ADMINEMAIL,
                    STATUS,
                    CANCELLEDBY,
                    INITIALCHARGE: Number(INITIALCHARGE)?.toFixed(2),
                    ADDITIONALCHARGE: Number(ADDITIONALCHARGE)?.toFixed(2),
                    ORDERNUMBER,
                    DATECREATED,
                    DATEUPDATED:
                        moment(DATEUPDATED).format("YYYY-MM-DD hh:mm A"),
                    CLIENTNAME,
                    CLIENTFIRSTNAME,
                    CLIENTLASTNAME,
                    CLIENTEMAIL,
                    CLIENTPHONENUMBER,
                    CLIENTNOTES,
                    PROVIDERNAME,
                    PROVIDERFIRSTNAME,
                    PROVIDERLASTNAME,
                    PROVIDEREMAIL,
                    PROVIDERPHONENUMBER,
                    RATING,
                    PROVIDERTIP,
                    NOTES
                };
                return newItem;
            });
            if (csvData && csvData.length) return csvData;
        } catch (e) {
            console.log(e);
        }
    };

    function enrichBookingData(item) {
        let bookingAddress = JSON.parse(item.order.bookingAddress);
        let TimeDisplayInfo = JSON.parse(item.TimeDisplayInfo);

        if (!isClientLoggedIn && item.provider?.timezone)
            TimeDisplayInfo = createTimeDisplayInfoForTimeZone(
                TimeDisplayInfo.dtstamp_str,
                item.provider?.timezone,
                item.minutes
            );
        try {
            item.Notes =
                typeof item.Notes === "string" && item.Notes.length > 0
                    ? escapeQuotes(JSON.parse(item.Notes))
                    : item.Notes
                      ? escapeQuotes(item.Notes)
                      : "";
        } catch (e) {
            item.Notes = "";
        }

        item.note = item.Notes ? escapeQuotes(item.Notes[0].notes) : "";
        item.serviceName = item.servicetype.name;
        item.startDateDesc = TimeDisplayInfo
            ? TimeDisplayInfo.dt_full_disp
            : format(
                  utcToZonedTime(
                      item.startdate,
                      item.timezone ? item.timezone : "America/Toronto"
                  ),
                  "MMM dd, yyyy h:mm a zzz",
                  {
                      timeZone: item.timezone
                          ? item.timezone
                          : "America/Toronto"
                  }
              );
        item.bookingTimeDesc = TimeDisplayInfo
            ? `${TimeDisplayInfo.tm_st_disp} \u2014 ${TimeDisplayInfo.tm_et_disp}`
            : `${moment
                  .tz(item.startdate, item?.timezone)
                  .format("h:mm A")} \u2014 ${moment
                  .tz(item.startdate, item?.timezone)
                  .add(item?.servicetype?.minutes, "minutes")
                  .format("h:mm A")}`;
        item.bookingDate = moment(item.startdate).format("YYYY-MM-DD");
        item.bookingTime = moment
            .tz(item.startdate, item.provider?.timezone)
            .format("h:mm A");
        item.orderNumber = item.order.orderNo;
        item.dateCreated = moment(item.createdAt).format("YYYY-MM-DD hh:mm A");
        item.duration = item.minutes;
        item.bookingAddressoneLine = bookingAddress
            ? bookingAddress.addrOneLine
            : "";
        item.bookingState = bookingAddress ? bookingAddress.state : "";
        item.bookingPostalCode = bookingAddress
            ? bookingAddress.postalCode
            : "";
        item.bookingCountry = bookingAddress ? bookingAddress.country : "";
        item.adminEmail =
            item.client.user.role === "MARKETBOX_ADMIN" ||
            item.client.user.role === "COMPANY_ADMIN" ||
            item.client.user.role === "COMPANY_ADMIN_PROVIDER"
                ? item.client.user.emailaddress
                : "";
        item.initialCharge = item.order.total;
        item.additionalCharges = item.totalAddChg ? item.totalAddChg : 0;
        item.clientName = `${
            item.client && item.client.user ? item.client.user.firstname : ""
        } ${item.client && item.client.user ? item.client.user.lastname : ""}`;
        item.clientFirstName = `${
            item.client && item.client.user ? item.client.user.firstname : ""
        }`;
        item.clientLastName = `${
            item.client && item.client.user ? item.client.user.lastname : ""
        }`;
        item.clientEmail =
            item.client && item.client.user && item.client.user.emailaddress
                ? item.client.user.emailaddress
                : "N/A";
        item.clientNotes =
            item.client &&
            item.client.Notes &&
            JSON.parse(item.client.Notes) &&
            "notes" in JSON.parse(item.client.Notes)
                ? escapeQuotes(JSON.parse(item.client.Notes).notes)
                : "";
        item.clientMobilePhone =
            item.client && item.client.user && item.client.user.mobilephone
                ? item.client.user.mobilephone
                : "N/A";
        item.clientHomePhone =
            item.client && item.client.user && item.client.user.homephone
                ? item.client.user.homephone
                : "N/A";
        item.clientWorkPhone =
            item.client && item.client.user && item.client.user.workphone
                ? item.client.user.workphone
                : "N/A";
        item.clientPhoneNumber = item.clientPhoneNumber = item.clientMobilePhone
            ? item.clientMobilePhone
            : item.clientHomePhone
              ? item.clientHomePhone
              : item.clientWorkPhone
                ? item.clientWorkPhone
                : "";
        item.providerName =
            item.provider &&
            `${item.provider.firstname} ${item.provider.lastname}`;
        item.providerFirstName = item.provider ? item.provider.firstname : "";
        item.providerLastName = item.provider ? item.provider.lastname : "";
        item.providerEmail = item.provider.emailaddress;
        item.providerPhoneNumber = item.provider.phone;
        item.cancelledBy
            ? (item.cancelledByEmail = item.cancelledBy.emailaddress)
            : (item.cancelledByEmail = " ");
        if (item.cancelledByEmail === item.clientEmail) {
            item.cancelledByEmail = "CLIENT";
        }
        if (item.cancelledByEmail === item.providerEmail) {
            item.cancelledByEmail = "PROVIDER";
        }
        item.ratingstars = item.rating ? item.rating.ratingstars : "";
        item.providerTip = item.totalAddChg;
        return item;
    }

    async function prepareExportData() {
        let startdateTimeblockid = {
            between: [
                {
                    startdate: `${moment(exportFromDate).format(
                        "YYYY-MM-DDT00:00:00Z"
                    )}`
                },
                {
                    startdate: `${moment(exportToDate).format(
                        "YYYY-MM-DDT23:59:59Z"
                    )}`
                }
            ]
        };

        const bookingsForExport = await execReadBySortkey({
            opname: "bookingByCompany",
            op: bookingByCompany,
            id: { companyId: mbxUser.company.id },
            skey: { startdateTimeblockid },
            sortDirection: "DESC"
        });

        let items = bookingsForExport.items || [];
        if (serviceTypeOption) {
            items = items.filter(
                (item) => item.servicetype.id === serviceTypeOption
            );
        }
        setExportReadyBookings(csvData({ items }));
    }

    const handlePrepareExport = async () => {
        setExportReadyBookings([]);
        setShowExportSpinner(true);
        setPreparingExportData(true);
        await prepareExportData();
        setPreparingExportData(false);
        setShowExportSpinner(false);
    };

    const handleClose = () => {
        setExportReadyBookings([]);
        setExportFromDate(null);
        setExportToDate(null);
        setPreparingExportData(false);
        setShowExportSpinner(false);
        if (onClose) onClose();
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm">
            <DialogTitle>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        sx={{ fontSize: "24px", fontWeight: 400 }}
                        variant="h4"
                    >
                        Export bookings
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent dividers style={{ padding: "0rem 1rem" }}>
                <Grid item xs={12}>
                    <Typography
                        variant="body1"
                        gutterBottom
                        style={{ marginBottom: "16px" }}
                    >
                        {`Please select a date range and click 'Prepare Export'.`}
                    </Typography>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ExportDateRangePicker
                            setFrom={setExportFromDate}
                            setTo={setExportToDate}
                            from={exportFromDate}
                            to={exportToDate}
                            style={{ margin: "8px" }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={preparingExportData}
                            onClick={handlePrepareExport}
                            style={{ marginTop: "24px" }}
                        >
                            Prepare Export
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        {preparingExportData && (
                            <Typography variant="body1">
                                {`We are preparing your bookings for export.`}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {showExportSpinner && <SmallSpinner />}
                    </Grid>
                    {exportReadyBookings && exportReadyBookings.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Please click the download button below to download your bookings.`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <CSVLink
                                    data={exportReadyBookings}
                                    headers={csvHeaders}
                                    filename={`bookings_${moment(
                                        Date.now()
                                    ).format("YYYY-MM-DD")}.csv`}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<GetAppIcon />}
                                    >
                                        Download
                                    </Button>
                                </CSVLink>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

export default ExportBookingList;
